<template>
    <CqModal
        :showModal="show"
        :preventClosing="isSaving"
        @closeModal="closeModal"
        heading="Stempels omzetten in diensten"
    >
        <CqCheckbox label="Iedereen" v-model="everyoneEnabled" />
        <template v-for="group in resourceGroups.availableGroups" :key="'group-' + (group.title ? group.title.id : 'notitle')">
            <hr />
            <template v-if="group.title !== null">
                <CqCheckbox :label="group.title.title" :modelValue="isEveryoneEnabled(group.resources)" @update:modelValue="toggleAll(group.resources)" />
                <hr />
            </template>
            <CqCheckbox
                class="resourceCheckbox"
                v-for="resource in group.resources"
                :key="resource.id"
                :label="getResourceDisplayName(resource) + (resource.employment?.employeeNumber ? (' (' + resource.employment.employeeNumber + ')') : '')"
                v-model="chosenResourcesList[resource.id!]"
            />
        </template>
        <template v-if="resourceGroups.disabled.length > 0"><b>Geen toestemming om in bewerking te zetten</b>:<br /><i>U heeft roosterontwikkelaarsrechten nodig voor de gehele periode waarin de medewerker in bewerking gezet zou worden.</i></template>
        <template v-for="resource in resourceGroups.disabled" :key="resource.id">
            <CqCheckbox :label="getResourceDisplayName(resource) + (resource.employment?.employeeNumber ? (' (' + resource.employment.employeeNumber + ')') : '')" disabled :modelValue="false" />
        </template>
        <CqButtonBar>
            <CqButton :disabled="isSaving" label="Annuleren" @click="closeModal" />
            <CqButton :disabled="isSaving" primary label="Bevestigen" @click="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqCheckbox, CqModal} from '@cquential/ui';
    import Notify from '@/components/modals/Notify.vue';
    import {ResourceGroupingStampDeployModal, ResourceGroupStampDeployModal, RosterData} from '@/types/types';
    import {ApiClient, Title, ApiError} from '@cquential/api';
    import {PlanbordResource} from '@/types/api-object-extensions';
    import Loader from '@/components/modals/Loader.vue';
    import {ResourceUtil} from '@/util/ResourceUtil';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqCheckbox,
            CqModal,
        },
        emits: ['reload:roster'],
    })
    export default class StampDeployModal extends Vue {
        private static instance: StampDeployModal | null;

        @Prop({required: true}) public roster!: RosterData;

        public deploymentAllowedForResourceList: Record<number, boolean> = {};
        public chosenResourcesList: Record<number, boolean> = {};
        public show: boolean = false;
        public date: Date = new Date();
        public isSaving: boolean = false;

        public mounted(): void {
            StampDeployModal.instance = this;
        }

        public beforeUnmount(): void {
            StampDeployModal.instance = null;
        }

        public get everyoneEnabled(): boolean {
            return !Object.values(this.chosenResourcesList).some((b: boolean): boolean => !b);
        }

        public set everyoneEnabled(value: boolean) {
            for (const resourceId in this.chosenResourcesList) {
                this.chosenResourcesList[resourceId] = value;
            }
        }

        public get resourceGroups(): ResourceGroupingStampDeployModal {
            const groups: ResourceGroupStampDeployModal[] = this.roster.titles!.map((title: Title): ResourceGroupStampDeployModal => ({title, resources: []}));
            const resources: ResourceGroupingStampDeployModal = {availableGroups: [], disabled: []};
            const availableResources: PlanbordResource[] = [];

            for (const resource of this.roster.rosterResources) {
                if (resource.id! in this.chosenResourcesList) {
                    availableResources.push(resource);
                } else {
                    resources.disabled.push(resource);
                }
            }

            if (groups.length === 0) {
                groups.push({
                    title: null,
                    resources: availableResources,
                });
            } else {
                this.addResourcesToGroups(groups, availableResources);
            }

            for (const group of groups) {
                group.resources.sort((a: PlanbordResource, b: PlanbordResource): number => a.rankInRoster - b.rankInRoster);
            }

            resources.availableGroups = groups;

            return resources;
        }

        private addResourcesToGroups(groups: ResourceGroupStampDeployModal[], resources: PlanbordResource[]): void {
            // sort reverse, so we check the lowest title first
            groups.sort((a: ResourceGroupStampDeployModal, b: ResourceGroupStampDeployModal): number => b.title!.rankInRoster - a.title!.rankInRoster);

            const resourcesWithoutGroup: PlanbordResource[] = [];
            for (const resource of resources) {
                const group: ResourceGroupStampDeployModal | undefined = groups.find((group: ResourceGroupStampDeployModal): boolean => group.title!.rankInRoster <= resource.rankInRoster);
                if (group) {
                    group.resources.push(resource);
                } else {
                    resourcesWithoutGroup.push(resource);
                }
            }

            // sort "correctly", for displaying
            groups.sort((a: ResourceGroupStampDeployModal, b: ResourceGroupStampDeployModal): number => a.title!.rankInRoster - b.title!.rankInRoster);

            if (resourcesWithoutGroup.length > 0) {
                groups.unshift({title: null, resources: resourcesWithoutGroup});
            }
        }

        public isEveryoneEnabled(resources: PlanbordResource[]): boolean {
            return resources.length === 0 || resources.every((resource: PlanbordResource): boolean => this.chosenResourcesList[resource.id!] ?? false);
        }

        public toggleAll(resources: PlanbordResource[]): void {
            const newValue: boolean = !this.isEveryoneEnabled(resources);

            for (const resource of resources) {
                this.chosenResourcesList[resource.id!] = newValue;
            }
        }

        public getResourceDisplayName(resource: PlanbordResource): string {
            return ResourceUtil.displayName(resource);
        }

        public save(): void {
            if (this.isSaving) {
                return;
            }

            const resourceIds: number[] = Object.keys(this.chosenResourcesList).map(Number).filter((key: number): boolean => this.chosenResourcesList[key]);

            this.isSaving = true;

            // Should be setLoading('stamps') but it's not available. Use roster to enforce opening of the loader
            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('roster');
            ApiClient.instance.deployStamps(resourceIds, this.date).then((): void => {
                this.$emit('reload:roster');
                // Handling of the pendingRoster is done in Planbord.vue
                // Should add setLoaded('stamps') when available.
                this.closeModal();
            }).catch((e: ApiError): void => {
                const error: string = 'Er ging iets mis... Vernieuw de pagina.';
                console.error(e);
                /** @see Loader.setError() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setError('roster');

                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: error, type: 'error'});
            }).finally((): void => {
                this.isSaving = false;
            });
        }

        public closeModal(): void {
            this.show = false;
        }

        public static open(openDeploymentAllowedForResourceList: Record<number, boolean>, openDate: Date): void {
            this.instance!.deploymentAllowedForResourceList = openDeploymentAllowedForResourceList;
            this.instance!.chosenResourcesList = {};
            this.instance!.show = true;
            this.instance!.date = openDate;

            for (const resourceId in openDeploymentAllowedForResourceList) {
                if (openDeploymentAllowedForResourceList[resourceId]) {
                    this.instance!.chosenResourcesList[resourceId] = true;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .resourceCheckbox {
        margin-bottom: 0.5rem;
    }
</style>
